import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface EstimationMethodology {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  complexity: 'low' | 'medium' | 'high';
}

const estimationMethodologies: EstimationMethodology[] = [
  {
    id: 't_shirt_sizing',
    name: 'T-Shirt Sizing',
    description: 'Simple story point estimation using XS, S, M, L, XL, XXL sizing',
    icon: <AssessmentIcon />,
    complexity: 'low'
  },
  {
    id: 'function_points',
    name: 'Function Point Analysis',
    description: 'IFPUG standard for measuring software functionality',
    icon: <FunctionsIcon />,
    complexity: 'high'
  },
  {
    id: 'cocomo',
    name: 'COCOMO Model',
    description: 'Constructive Cost Model for effort estimation',
    icon: <TimelineIcon />,
    complexity: 'high'
  }
];

const AdvancedEstimation: React.FC = () => {
  const [selectedMethodology, setSelectedMethodology] = useState<string>('t_shirt_sizing');
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEstimationConfigs();
      setConfigs(response || []);
    } catch (err) {
      setError('Failed to load estimation configurations');
    } finally {
      setLoading(false);
    }
  };

  const getMethodologyColor = (complexity: string) => {
    switch (complexity) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const renderMethodologyDetails = () => {
    switch (selectedMethodology) {
      case 'function_points':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Function Point Analysis (IFPUG Standard)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Function Point Analysis measures software functionality from the user's perspective. 
                It counts external inputs, outputs, inquiries, internal logical files, and external interface files.
              </Typography>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">External Inputs (EI)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Data or control information that comes from outside the boundary and enters the system.
                    Examples: User registration forms, file uploads, API calls from external systems.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">External Outputs (EO)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Data or control information that leaves the system boundary.
                    Examples: Reports, emails, API responses, file downloads.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">External Inquiries (EQ)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Elementary processes that result in data retrieval from one or more internal logical files.
                    Examples: Search functionality, data lookups, status checks.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Internal Logical Files (ILF)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    User identifiable group of logically related data that resides entirely within the boundary.
                    Examples: User profiles, product catalog, order history.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">External Interface Files (EIF)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    User identifiable group of logically related data that is referenced by the system but maintained outside the boundary.
                    Examples: Third-party APIs, external databases, payment gateways.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        );

      case 'cocomo':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                COCOMO Model (Constructive Cost Model)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                COCOMO is a regression model based on project size and cost drivers. It estimates effort, 
                development time, and staffing levels for software projects.
              </Typography>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Project Types</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="primary">Organic</Typography>
                      <Typography variant="body2">
                        Small teams with good experience working on less rigid requirements.
                        Examples: Small business applications, simple web apps.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="warning.main">Semi-detached</Typography>
                      <Typography variant="body2">
                        Medium teams with mixed experience levels and moderate requirements.
                        Examples: Most commercial software, mobile applications.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="error">Embedded</Typography>
                      <Typography variant="body2">
                        Large teams with strict requirements and constraints.
                        Examples: Real-time systems, safety-critical software.
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Cost Drivers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" paragraph>
                    COCOMO uses 15 cost drivers to adjust the basic effort estimate:
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      'Product Complexity', 'Required Reliability', 'Database Size',
                      'Execution Time Constraint', 'Memory Constraint', 'Virtual Machine Volatility',
                      'Computer Turnaround Time', 'Analyst Capability', 'Applications Experience',
                      'Programmer Capability', 'Virtual Machine Experience', 'Programming Language Experience',
                      'Modern Programming Practices', 'Software Tools', 'Required Development Schedule'
                    ].map((driver, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Chip label={driver} size="small" variant="outlined" />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        );

      default:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                T-Shirt Sizing
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                T-shirt sizing is a simple and effective estimation technique that uses relative sizing 
                to categorize stories by complexity.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="XS (1 point)" color="success" />
                  <Typography variant="body2">Very simple, can be done in a few hours</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="S (2 points)" color="info" />
                  <Typography variant="body2">Simple, can be done in half a day</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="M (5 points)" color="warning" />
                  <Typography variant="body2">Medium complexity, can be done in a day</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="L (8 points)" color="error" />
                  <Typography variant="body2">Large, can be done in 2-3 days</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="XL (13 points)" color="error" />
                  <Typography variant="body2">Extra large, can be done in a week</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Chip label="XXL (21 points)" color="error" />
                  <Typography variant="body2">Too large, should be broken down</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Advanced Estimation Models
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Choose from different estimation methodologies based on your project needs and team expertise.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Methodology Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Estimation Methodologies
              </Typography>
              <Box sx={{ mb: 2 }}>
                {estimationMethodologies.map((methodology) => (
                  <Card
                    key={methodology.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      border: selectedMethodology === methodology.id ? 2 : 1,
                      borderColor: selectedMethodology === methodology.id ? 'primary.main' : 'divider'
                    }}
                    onClick={() => setSelectedMethodology(methodology.id)}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {methodology.icon}
                        <Box flex={1}>
                          <Typography variant="subtitle2">
                            {methodology.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {methodology.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={methodology.complexity}
                          color={getMethodologyColor(methodology.complexity) as any}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Methodology Details */}
        <Grid item xs={12} md={8}>
          {renderMethodologyDetails()}
        </Grid>

        {/* Existing Configurations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Existing Configurations
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : configs.length > 0 ? (
                <Grid container spacing={2}>
                  {configs.map((config) => (
                    <Grid item xs={12} sm={6} md={4} key={config.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {config.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {config.description}
                          </Typography>
                          <Chip
                            label={config.methodology}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No estimation configurations found. Create one to get started.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdvancedEstimation;
